import { STORAGE_KEYS, TAX_PREFERENCES, CHARGE_CALCULATION_TYPES, CHARGE_APPLY_ON, CHARGE_CATEGORIES } from "../const";
import * as NumberFormtHelper from "helpers/NumberFormatHelper";

class TaxHelper {
  getTaxPreference() {
    return localStorage.getItem(STORAGE_KEYS.TAX_PREFERENCE);
  }

  /**
   * Resolves the undiscounted base unit price of a saleItem before discounts and tax extraction.
   * Priority:
   * 1. manualRate (cashier explicit price override)
   * 2. priceCategoryRate (active price category tier rate)
   * 3. option.price (product variant price)
   * 4. item.price / price (catalog product price)
   * 5. Fallback for detached/synced items without catalog reference:
   *    - For INCLUDING_TAX: uses rateAndTax (the inclusive unit rate).
   *    - For EXCLUDING_TAX: uses rate (the exclusive unit rate).
   *
   * NOTE: Base catalog/manual rates are already defined according to the shop's tax preference.
   * Never multiply catalog/manual rates by (1 + tax) as that causes double-tax inflation.
   */
  getSaleItemBaseRate(saleItem) {
    const directRate =
      saleItem?.manualRate ?? saleItem?.priceCategoryRate ?? saleItem?.option?.price ?? saleItem?.item?.price;
    if (directRate != null) {
      return directRate;
    }

    const isExcluding = this.getTaxPreference() === TAX_PREFERENCES.EXCLUDING_TAX;
    return (isExcluding ? saleItem?.rate : saleItem?.rateAndTax) ?? saleItem?.rateAndTax ?? saleItem?.rate ?? 0;
  }

  getItemUnitRate(price, taxPercentage = 0, cessPercentage = 0, discountPercentage = 0) {
    const discount = discountPercentage || 0;

    let effectivePrice = price;
    if (discount > 0 && discount <= 100) {
      effectivePrice = (price * (100 - discount)) / 100;
    }

    const taxPreference = this.getTaxPreference();

    if (taxPreference === TAX_PREFERENCES.INCLUDING_TAX) {
      const totalTaxPercentage = taxPercentage + cessPercentage;

      if (totalTaxPercentage > 0) {
        return NumberFormtHelper.amountExtendedRoundOff(
          (effectivePrice = (effectivePrice * 100) / (100 + totalTaxPercentage)),
        );
      }
    }

    return NumberFormtHelper.amountExtendedRoundOff(effectivePrice);
  }

  getItemCess(cessPercentage, amount) {
    const taxPreference = this.getTaxPreference();
    if (taxPreference === TAX_PREFERENCES.NO_TAX) {
      return 0;
    }

    return NumberFormtHelper.amountExtendedRoundOff((amount * cessPercentage) / 100);
  }

  getItemTax(taxPercentage, amount) {
    const taxPreference = this.getTaxPreference();
    if (taxPreference === TAX_PREFERENCES.NO_TAX) {
      return 0;
    }

    return NumberFormtHelper.amountExtendedRoundOff((amount * taxPercentage) / 100);
  }

  getSaleItemTaxAndCess(rate, taxPercentage = 0, cessPercentage = 0) {
    const taxPreference = this.getTaxPreference();

    if (taxPreference === TAX_PREFERENCES.NO_TAX) {
      return 0;
    }

    // Same for including and excluding
    const tax = (rate * (taxPercentage + cessPercentage)) / 100;
    return NumberFormtHelper.amountExtendedRoundOff(tax);
  }

  getItemCharges(saleItem, charges, selectedChargeGroupIds = null) {
    const item = saleItem.item;
    let itemChargeGroups = Array.isArray(item?.charge_groups) ? item.charge_groups : [];

    if (selectedChargeGroupIds) {
      itemChargeGroups = itemChargeGroups.filter((id) => selectedChargeGroupIds.includes(Number(id)));
    }

    if (!itemChargeGroups.length || !charges?.length) {
      return [];
    }

    const itemTotal = saleItem.rate * (saleItem.qty || 1);
    const groupCharges = charges
      .filter((c) => itemChargeGroups.includes(c.group?.id))
      .sort((a, b) => a.order - b.order);

    const appliedCharges = [];
    let currentTotal = itemTotal;

    groupCharges.forEach((charge) => {
      // Check amount range if specified
      const minAmount = Number(charge.min_amount || 0);
      const maxAmount = Number(charge.max_amount || 0);

      if (minAmount > 0 && itemTotal < minAmount) return;
      if (maxAmount > 0 && itemTotal > maxAmount) return;

      let chargeValue = 0;
      if (charge.calculation_type === CHARGE_CALCULATION_TYPES.PERCENT) {
        const baseForCharge = charge.apply_on === CHARGE_APPLY_ON.PREV ? currentTotal : itemTotal;
        chargeValue = (baseForCharge * charge.value) / 100;
      } else {
        // AMOUNT
        chargeValue = charge.value;
      }

      chargeValue = NumberFormtHelper.amountRoundOff(chargeValue);

      appliedCharges.push({
        id: charge.id,
        name: charge.name,
        value: chargeValue,
        category: charge.category,
      });

      if (charge.category === CHARGE_CATEGORIES.DISCOUNT) {
        currentTotal -= chargeValue;
      } else {
        currentTotal += chargeValue;
      }
    });

    return appliedCharges;
  }

  getSaleCharges(saleTotal, tax, charges, chargeGroupIds) {
    if (!chargeGroupIds || !charges?.length) {
      return [];
    }

    const groupIds = Array.isArray(chargeGroupIds) ? chargeGroupIds : [chargeGroupIds];
    if (groupIds.length === 0) return [];

    const taxPreference = this.getTaxPreference();
    const isIncludingTax = taxPreference === TAX_PREFERENCES.INCLUDING_TAX;
    const grossTotal = Number(saleTotal || 0) + Number(tax || 0);
    const totalForApplicability = grossTotal;
    const initialBase = isIncludingTax ? grossTotal : Number(saleTotal || 0);

    const groupCharges = charges.filter((c) => groupIds.includes(c.group?.id)).sort((a, b) => a.order - b.order);

    const appliedCharges = [];
    let currentTotal = initialBase;

    groupCharges.forEach((charge) => {
      const minAmount = Number(charge.min_amount || 0);
      const maxAmount = Number(charge.max_amount || 0);

      if (minAmount > 0 && totalForApplicability < minAmount) return;
      if (maxAmount > 0 && totalForApplicability > maxAmount) return;

      let chargeValue = 0;
      if (charge.calculation_type === CHARGE_CALCULATION_TYPES.PERCENT) {
        const baseForCharge = charge.apply_on === CHARGE_APPLY_ON.PREV ? currentTotal : initialBase;
        chargeValue = (baseForCharge * charge.value) / 100;
      } else {
        // AMOUNT
        chargeValue = charge.value;
      }

      chargeValue = NumberFormtHelper.amountRoundOff(chargeValue);

      appliedCharges.push({
        id: charge.id,
        name: charge.name,
        value: chargeValue,
        category: charge.category,
      });

      if (charge.category === CHARGE_CATEGORIES.DISCOUNT) {
        currentTotal -= chargeValue;
      } else {
        currentTotal += chargeValue;
      }
    });

    return appliedCharges;
  }

  getRateAndRateAndTax(rate, saleItem) {
    const taxPref = this.getTaxPreference();

    // No tax
    if (taxPref === TAX_PREFERENCES.NO_TAX) {
      return { rate: rate, rateAndTax: rate };
    }

    // Including
    if (taxPref === TAX_PREFERENCES.INCLUDING_TAX) {
      const includingRate = this.getItemUnitRate(
        rate,
        Number(saleItem?.taxPercentage) || 0,
        Number(saleItem?.cessPercentage) || 0,
      );
      return { rate: includingRate, rateAndTax: rate };
    }

    // Excluding
    const taxAndCess = this.getSaleItemTaxAndCess(
      rate,
      Number(saleItem?.taxPercentage) || 0,
      Number(saleItem?.cessPercentage) || 0,
    );
    return { rate: rate, rateAndTax: rate + taxAndCess };
  }
}

const taxHelper = new TaxHelper();
export default taxHelper;
